import { invariantResponse } from '@epic-web/invariant'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, useFetcher, useLoaderData, type MetaFunction } from '@remix-run/react'
import { brandName } from '#app/components/branding.js'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { rarityColors, TYCOON_DESCRIPTION_PREFIX, type AchievementRarity } from '#app/utils/achievements.js'
import { checkAndAwardAchievements, getAchievementsForProfile } from '#app/utils/achievements.server.js'
import { getUserId } from '#app/utils/auth.server.js'
import { prisma } from '#app/utils/db.server.ts'
import { getUserImgSrc } from '#app/utils/misc.tsx'
import { momentsAgo } from '#app/utils/dateFormat.js'

export async function loader({ params, request }: LoaderFunctionArgs) {
	const owner = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			pinnedAchievementSlug: true,
			image: { select: { id: true } },
		},
		where: { username: params.username },
	})

	invariantResponse(owner, 'Owner not found', { status: 404 })

	const viewerId = await getUserId(request)
	const isOwner = viewerId === owner.id

	// Award any new achievements if viewing own profile
	if (isOwner) {
		await checkAndAwardAchievements(owner.id)
	}

	const achievements = await getAchievementsForProfile(owner.id)
	const unlockedCount = achievements.filter((a) => a.unlocked).length

	return json({ owner, achievements, unlockedCount, rarityColors, isOwner, pinnedSlug: owner.pinnedAchievementSlug })
}

type ProfileAchievement = {
	slug: string
	name: string
	description: string
	icon: string
	unlocked: boolean
	unlockedAt: string | null
}

function AchievementCard({
	achievement: a,
	colors: rc,
	isPinned,
	isOwner,
	onPin,
}: {
	achievement: ProfileAchievement
	colors: { bg: string; text: string; border: string }
	isPinned: boolean
	isOwner: boolean
	onPin: (slug: string) => void
}) {
	return (
		<div
			className={`flex items-start gap-3 rounded-xl border p-3 md:p-4 ${
				isPinned
					? `${rc.bg} ${rc.border} ring-2 ${rc.border.replace('border-', 'ring-')}`
					: a.unlocked
						? `${rc.bg} ${rc.border}`
						: 'bg-muted/20 border-muted-foreground/10 opacity-50'
			}`}
		>
			<span className="text-2xl md:text-3xl">{a.icon}</span>
			<div className="min-w-0 flex-1">
				<div className="flex items-center gap-1.5">
					<h3 className={`text-sm font-semibold md:text-base ${a.unlocked ? rc.text : 'text-muted-foreground'}`}>
						{a.name}
					</h3>
					{isPinned && (
						<span className="text-[10px] rounded-full bg-muted-foreground/10 px-1.5 py-0.5">pinned</span>
					)}
				</div>
				<p className="text-xs text-muted-foreground mt-0.5">
					{a.description}
				</p>
				{a.unlocked && a.unlockedAt && (
					<p className="text-[10px] text-muted-foreground mt-1">
						Unlocked {momentsAgo(a.unlockedAt.toString())} ago
					</p>
				)}
			</div>
			{a.unlocked && isOwner ? (
				<button
					onClick={() => onPin(a.slug)}
					className={`ml-auto text-sm transition-colors ${
						isPinned
							? 'text-amber-500 hover:text-amber-600'
							: 'text-muted-foreground/40 hover:text-amber-500'
					}`}
					title={isPinned ? 'Unpin from profile' : 'Pin to profile'}
				>
					📌
				</button>
			) : a.unlocked ? (
				<span className="ml-auto text-green-600 dark:text-green-400 text-sm">✓</span>
			) : null}
		</div>
	)
}

export default function UserAchievementsRoute() {
	const { owner, achievements, unlockedCount, rarityColors: colors, isOwner, pinnedSlug } = useLoaderData<typeof loader>()
	const pinFetcher = useFetcher()
	const optimisticPinned = pinFetcher.formData
		? (pinFetcher.formData.get('slug') as string) || null
		: pinnedSlug

	const handlePin = (slug: string) => {
		const newSlug = optimisticPinned === slug ? '' : slug
		pinFetcher.submit(
			{ slug: newSlug },
			{ method: 'POST', action: '/resources/pin-achievement' },
		)
	}

	const ownerDisplayName = owner.name ?? owner.username

	const coreAchievements = achievements.filter((a) => !a.bonus)
	const tycoonAchievements = achievements
		.filter((a) => a.group === 'tycoon')
		.map((a) => ({
			...a,
			// The heading already says which game these are from.
			description: a.description.replace(TYCOON_DESCRIPTION_PREFIX, ''),
		}))
	const bonusAchievements = achievements.filter((a) => a.bonus && !a.group)
	const sections = [
		{
			title: 'Vendor Tycoon',
			note: (
				<>
					Earned by playing{' '}
					<Link to="/tycoon" className="underline">
						Vendor Tycoon
					</Link>{' '}
					— these don't count toward your progress bar.
				</>
			),
			achievements: tycoonAchievements,
		},
		{
			title: 'Bonus Badges',
			note: "Special badges for milestones and events — these don't count toward your progress bar.",
			achievements: bonusAchievements,
		},
	]
	const coreUnlocked = coreAchievements.filter((a) => a.unlocked).length

	const groupedByRarity: Record<string, typeof achievements> = {}
	for (const a of coreAchievements) {
		if (!groupedByRarity[a.rarity]) groupedByRarity[a.rarity] = []
		groupedByRarity[a.rarity]!.push(a)
	}

	const rarityOrder: AchievementRarity[] = ['legendary', 'epic', 'rare', 'common']
	const rarityLabels: Record<string, string> = {
		legendary: 'Legendary',
		epic: 'Epic',
		rare: 'Rare',
		common: 'Common',
	}

	return (
		<div className="container mx-auto mb-24 px-4 pt-8 md:mb-48">
			{/* Header */}
			<div className="flex items-center gap-3">
				<Link to={`/users/${owner.username}`}>
					<img
						src={getUserImgSrc(owner.image?.id)}
						alt={ownerDisplayName}
						className="h-10 w-10 rounded-full object-cover md:h-12 md:w-12"
					/>
				</Link>
				<div>
					<Link to={`/users/${owner.username}`} className="text-sm text-muted-foreground hover:underline">
						&larr; {ownerDisplayName}
					</Link>
					<h1 className="text-xl font-bold md:text-2xl">
						Achievements ({coreUnlocked} / {coreAchievements.length})
					</h1>
				</div>
			</div>

			{/* Progress bar */}
			<div className="mt-6">
				<div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
					<span>{coreAchievements.length > 0 ? Math.round((coreUnlocked / coreAchievements.length) * 100) : 0}% complete</span>
					<span>{coreAchievements.length - coreUnlocked} remaining</span>
				</div>
				<div className="h-2 w-full rounded-full bg-muted">
					<div
						className="h-2 rounded-full bg-blue-600 transition-all"
						style={{ width: `${coreAchievements.length > 0 ? (coreUnlocked / coreAchievements.length) * 100 : 0}%` }}
					/>
				</div>
			</div>

			{/* Achievements by rarity */}
			<div className="mt-8 space-y-8">
				{rarityOrder.map((rarity) => {
					const group = groupedByRarity[rarity]
					if (!group || group.length === 0) return null
					const rc = colors[rarity as keyof typeof colors] ?? colors.common
					const groupUnlocked = group.filter((a) => a.unlocked).length

					return (
						<div key={rarity}>
							<div className="flex items-center gap-2 mb-3">
								<h2 className={`text-sm font-semibold uppercase tracking-wider ${rc.text}`}>
									{rarityLabels[rarity]}
								</h2>
								<span className="text-xs text-muted-foreground">
									{groupUnlocked} / {group.length}
								</span>
							</div>
							<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
								{group.map((a) => (
									<AchievementCard
										key={a.slug}
										achievement={a}
										colors={rc}
										isPinned={optimisticPinned === a.slug}
										isOwner={isOwner}
										onPin={handlePin}
									/>
								))}
							</div>
						</div>
					)
				})}

				{sections.map((section) =>
					section.achievements.length === 0 ? null : (
						<div key={section.title}>
							<div className="flex items-center gap-2 mb-3">
								<h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
									{section.title}
								</h2>
								<span className="text-xs text-muted-foreground">
									{section.achievements.filter((a) => a.unlocked).length} / {section.achievements.length}
								</span>
							</div>
							<p className="text-xs text-muted-foreground mb-3">
								{section.note}
							</p>
							<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
								{section.achievements.map((a) => (
									<AchievementCard
										key={a.slug}
										achievement={a}
										colors={colors[a.rarity as keyof typeof colors] ?? colors.common}
										isPinned={optimisticPinned === a.slug}
										isOwner={isOwner}
										onPin={handlePin}
									/>
								))}
							</div>
						</div>
					),
				)}
			</div>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.owner.name ?? params.username
	return [
		{ title: `${displayName}'s Achievements | ${brandName}` },
		{ name: 'description', content: `${displayName}'s achievements and badges on ${brandName}` },
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => <p>No user with the username "{params.username}" exists</p>,
			}}
		/>
	)
}
